// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴🔴 FECHAR UMA CAIXA ABERTA SEM GRAVAR — UMA FORMULAÇÃO, TODOS OS CAMINHOS
// ============================================================================
//
// 📗 **Decisão do cliente, 2026-09-13:** *«os dois botões passam a um»*. O par «Está certo» +
//    «Deixar como está» custava dois controlos em cada caixa aberta, e o segundo existia só
//    para dar saída ao primeiro.
//
// ⚠️⚠️ **Tirar o botão sem mais deixa a caixa SEM SAÍDA — visível nem invisível.** Um gesto sem
//    saída é um beco, e é pior do que o botão a mais. Por isso as duas saídas entram no mesmo
//    movimento em que o botão sai:
//
//    1. **`Escape`** — no CONTENTOR da caixa e não só no `<input>`: com o foco no botão de
//       decisão (um `Tab`), `Escape` tem de continuar a fechar.
//    2. **Clicar fora** — um `mousedown` no `document`, comparado contra o `ref` do contentor.
//
// ⚠️⚠️ **E ISTO VIVE NUM MÓDULO PRÓPRIO POR CAUSA DA REGRA 8.** A Task 8 escreveu-o **dentro**
//    do `ValorEditavel`, e nesse dia o comportamento existia num dos dois ramos desse ficheiro
//    e em nenhum dos outros componentes que abrem a mesma caixa. 📌 *«Corrigir todas, com UMA
//    SÓ formulação partilhada — duas formulações divergem, e a que divergir é a que ninguém
//    corre.»* Quem abrir uma caixa nova espalha isto e tem as duas saídas de graça.
//
// ⚠️ **O `fechar` guarda-se num `ref` e a lista de dependências é VAZIA — de propósito.** O
//    `useEffect` que apanhou este projecto (2026-08-12, `mudarCondutor`) prendeu valores
//    iniciais numa `closure` porque a lista estava **incompleta**; aqui não há valor preso
//    nenhum: o ouvinte lê sempre `fecharRef.current`, que é reescrito em cada render. Pôr
//    `[fechar]` reataria o ouvinte a cada render, porque uma função nova nasce a cada render.
// ============================================================================

import { useEffect, useRef } from 'react';

/**
 * O que se espalha (`{...saida}`) no contentor da caixa aberta.
 *
 * ⚠️ O `ref` é de `HTMLSpanElement` porque é o que as duas caixas de hoje usam (`<span
 * className="proposta-valor-edicao">`); um contentor de outro tipo alarga este tipo em vez de
 * criar um segundo `hook`.
 */
export interface SaidaDaCaixa {
  readonly ref: React.RefObject<HTMLSpanElement | null>;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * 🔴🔴 **As duas saídas de uma caixa aberta: `Escape` e clicar fora.**
 *
 * ⚠️⚠️ **Chama-se INCONDICIONALMENTE, no topo do componente.** Os componentes que abrem estas
 * caixas devolvem cedo em vários ramos antes de chegarem à caixa; as regras dos *hooks* exigem
 * a mesma ordem em todos os renders. 📌 Não precisa de saber SE a caixa está aberta — o
 * `ref.current` só existe montado quando ela está, e é essa a condição real.
 *
 * @param fechar o que fazer para fechar sem gravar (é sempre só estado do ecrã: **nada** aqui
 *   escreve na base, e é isso que faz de `Escape` e do clique fora saídas e não decisões).
 */
export function useSaidaDaCaixa(fechar: () => void): SaidaDaCaixa {
  const ref = useRef<HTMLSpanElement | null>(null);
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;

  useEffect(() => {
    const aoClicarFora = (e: MouseEvent): void => {
      const el = ref.current;
      // ⚠️ A metade que se esquece: `contains` — sem ela, um `mousedown` **dentro** da caixa
      //    fecha-a, e carregar no próprio botão de gravar passa a ser impossível.
      if (el !== null && !el.contains(e.target as Node)) fecharRef.current();
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  return {
    ref,
    onKeyDown: (e: React.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        fecharRef.current();
      }
    },
  };
}
